/**
 * Creates a consumable map that groups items by key and allows consuming them in order.
 * Handles duplicate keys by storing values in an array.
 *
 * @param items - Array of items to group
 * @param getKey - Function to extract the key from each item
 * @param getValue - Function to extract the value from each item
 * @returns Map where each key points to an array of values
 *
 * @example
 * const items = [
 *   { name: 'file.pdf', id: 1 },
 *   { name: 'file.pdf', id: 2 },
 *   { name: 'doc.txt', id: 3 }
 * ];
 * const map = createConsumableMap(items, item => item.name, item => item.id);
 * // Map { 'file.pdf' => [1, 2], 'doc.txt' => [3] }
 */
export const createConsumableMap = <TItem, TValue>(
  items: TItem[],
  getKey: (item: TItem) => string,
  getValue: (item: TItem) => TValue,
): Map<string, TValue[]> => {
  const map = new Map<string, TValue[]>();
  for (const item of items) {
    const key = getKey(item);
    const existing = map.get(key) || [];
    existing.push(getValue(item));
    map.set(key, existing);
  }
  return map;
};

/**
 * Consumes (gets and removes) the next value from the map for a given key.
 * Returns undefined if no values exist for the key.
 *
 * @param map - The consumable map
 * @param key - The key to get the value for
 * @returns The next value for the key, or undefined if none exist
 *
 * @example
 * const map = new Map([['file.pdf', [1, 2]], ['doc.txt', [3]]]);
 * consumeFromMap(map, 'file.pdf'); // 1, map now has ['file.pdf' => [2]]
 * consumeFromMap(map, 'file.pdf'); // 2, map now has ['file.pdf' => []]
 * consumeFromMap(map, 'file.pdf'); // undefined
 */
export const consumeFromMap = <TValue>(
  map: Map<string, TValue[]>,
  key: string,
): TValue | undefined => {
  const values = map.get(key) || [];
  return values.shift();
};
